import React, { useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import type { OrderType } from '../../types/orderTypes';
import { formattingDate } from '../../utils/formattingDateHelper';
import { defaultTheme } from '../../styles/theme';
import config from '../../config';
import { useAppSelector } from '../../store/hooks';

type PropsType = {
  order: OrderType;
  onCloseModal: () => void;
};

const {
  color: {
    background,
    typography: { blackText, greyText },
    grey: { background: greyBorder, lightBackground },
  },
} = defaultTheme.mainTheme;

const headerCellSx = {
  textAlign: 'center',
  border: 'none',
  padding: '10px 15px',
  backgroundColor: lightBackground,
  borderTop: `1px solid ${greyBorder}`,
  borderBottom: `1px solid ${greyBorder}`,
  fontSize: 15,
  fontWeight: 300,
  color: greyText,
  whiteSpace: 'nowrap',
  '&:first-of-type': {
    borderRadius: '4px 0 0 0',
    borderLeft: `1px solid ${greyBorder}`,
    color: blackText,
  },
  '&:last-of-type': {
    borderRadius: '0 4px 0 0',
    borderRight: `1px solid ${greyBorder}`,
  },
} as const;

const bodyCellSx = {
  color: blackText,
  textAlign: 'center',
  padding: '13px 5px',
  borderBottom: `1px solid ${greyBorder}`,
  fontSize: 15,
  fontWeight: 300,
  whiteSpace: 'nowrap',
  borderTop: 'none',
  '&:first-of-type': {
    borderLeft: `1px solid ${greyBorder}`,
  },
  '&:last-of-type': {
    borderRight: `1px solid ${greyBorder}`,
  },
} as const;

const detailLabelSx = {
  color: greyText,
  fontSize: 13,
  fontWeight: 400,
} as const;

const detailValueSx = {
  color: blackText,
  fontSize: 14,
  fontWeight: 600,
  wordBreak: 'break-word',
} as const;

const OrderTransactionsModal: React.FC<PropsType> = ({
  order,
  onCloseModal,
}) => {
  const userInfo = useAppSelector((state) => state.userSlice.user);
  const transactions = useMemo(
    () => order.transactions ?? [],
    [order.transactions],
  );

  const orderDetails = useMemo(() => {
    const totalRecords = order.totalRecords || 0;
    const successRecords = order.successfullRecords || 0;
    const failedRecords = order.failedRecords || 0;
    const emailHits = order.emailHits || 0;
    const emailEnabled = Boolean(order.metadata?.email);
    const ownerSubscriptionType = (
      order.user?.subscription?.type || userInfo.subscription?.type || ''
    ).toLowerCase();
    const isEmailFree = config.freeEmailSubscriptions.includes(ownerSubscriptionType);
    const emailPricePerRecord = emailEnabled && !isEmailFree
      ? config.emailPricePerRecord
      : 0;
    const failedEmailHits = emailEnabled && (order.status || '').toLowerCase() === 'successful'
      ? Math.max(0, totalRecords - emailHits)
      : 0;

    const emailRefundTotal = transactions
      .filter((tx) => (tx.transferType || '').toUpperCase() === 'IN' && (tx.description || '').toLowerCase().includes('returned balance for unsuccessful email records'))
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    const skipRefundTotal = transactions
      .filter((tx) => (tx.transferType || '').toUpperCase() === 'IN' && (tx.description || '').toLowerCase().includes('returned balance for unsuccessful records'))
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    const orderCreatedTx = transactions.find(
      (tx) => (tx.description || '').toLowerCase().includes('order created'),
    );
    const refundTotal = emailRefundTotal + skipRefundTotal;
    const prepaidPrice = orderCreatedTx
      ? Number(orderCreatedTx.amount) || 0
      : (Number(order.price) || 0) + refundTotal;
    const emailPrice = emailPricePerRecord * totalRecords;
    const skipPrice = Math.max(0, prepaidPrice - emailPrice);
    const skipPricePerRecord = totalRecords > 0
      ? skipPrice / totalRecords
      : 0;

    return {
      totalRecords,
      successRecords,
      failedRecords,
      emailHits,
      failedEmailHits,
      emailEnabled,
      emailPricePerRecord,
      skipPricePerRecord,
      prepaidPrice,
      finalPrice: Number(order.price) || 0,
      refundTotal,
      emailRefundTotal,
      skipRefundTotal,
      emailPrice,
      skipPrice,
    };
  }, [order, transactions, userInfo.subscription?.type]);

  const detailCategories = useMemo(() => {
    const categories = [
      {
        title: 'ORDER INFO:',
        items: [
          { label: 'Order Name', value: order.name.slice(0, 20) + (order.name.length > 20 ? '...' : '') },
          { label: 'Skip Trace Type', value: order.type || '-' },
          { label: 'Status', value: order.status },
          { label: 'Total Records', value: orderDetails.totalRecords },
        ],
      },
      {
        title: 'SKIPTRACE RECORDS:',
        items: [
          { label: 'Hit Records (Success)', value: orderDetails.successRecords },
          { label: 'Failed Records', value: orderDetails.failedRecords },
          {
            label: 'Skiptrace Price / Record',
            value: `$${orderDetails.skipPricePerRecord.toFixed(3)}`,
          },
          {
            label: 'Total Skiptrace Price',
            value: `$${orderDetails.skipPrice.toFixed(2)}`,
          },
        ],
      },
      ...(orderDetails.emailEnabled
        ? [{
          title: 'EMAIL RECORDS:',
          items: [
            { label: 'Email Hits (Success)', value: orderDetails.emailHits },
            { label: 'Failed Email Hits', value: orderDetails.failedEmailHits },
            {
              label: 'Email Price / Record',
              value: `$${orderDetails.emailPricePerRecord.toFixed(2)}`,
            },
            {
              label: 'Total Email Price',
              value: `$${orderDetails.emailPrice.toFixed(2)}`,
            },
          ],
        }]
        : []),
      {
        title: 'PRICING:',
        items: [
          ...(orderDetails.emailEnabled
            ? [{
              label: 'Email Refunded',
              value: `$${orderDetails.emailRefundTotal.toFixed(2)}`,
            }]
            : []),
          {
            label: 'Skiptrace Refunded',
            value: `$${orderDetails.skipRefundTotal.toFixed(2)}`,
          },
          {
            label: 'Prepaid Total',
            value: `$${orderDetails.prepaidPrice.toFixed(2)}`,
          },
          {
            label: 'Total Refunded',
            value: `$${orderDetails.refundTotal.toFixed(2)}`,
          },
          {
            label: 'Total Price',
            value: `$${orderDetails.finalPrice.toFixed(2)}`,
          },
        ],
      },
    ];
    return categories;
  }, [order.name, order.type, order.status, orderDetails]);

  return (
    <Modal
      open
      onClose={onCloseModal}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 820,
          maxHeight: '85vh',
          borderRadius: 3,
          p: 3,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          backgroundColor: background,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: 24,
                fontWeight: 900,
                lineHeight: '36px',
                letterSpacing: '0.03em',
                color: blackText,
              }}
            >
              ORDER DETAILS
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: greyText,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              #{order.userFileId} — {order.name}
            </Typography>
          </Box>
          <IconButton onClick={onCloseModal} size="small" aria-label="Close">
            <CloseIcon width="16px" height="16px" />
          </IconButton>
        </Box>

          {detailCategories.map((category) => (
            <Box key={category.title} sx={{
              border: `1px solid ${greyBorder}`,
              borderRadius: 2,
              p: 2,
              backgroundColor: lightBackground,
            }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: blackText,
                  mb: 1,
                  letterSpacing: '0.02em',
                }}
              >
                {category.title}
              </Typography>
              <Grid container spacing={1.5}>
                {category.items.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Typography sx={detailLabelSx}>{item.label}</Typography>
                    <Typography sx={detailValueSx}>{item.value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: blackText,
            letterSpacing: '0.02em',
          }}
        >
          ORDER TRANSACTIONS
        </Typography>
        {transactions.length === 0 ? (
          <Typography variant="body2" sx={{ color: greyText, textAlign: 'center' }}>
            No transactions found for this order.
          </Typography>
        ) : (
          <TableContainer
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'visible',
              borderRadius: '10px',
              backgroundColor: background,
            }}
          >
            <Table
              sx={{
                minWidth: 520,
                borderCollapse: 'separate',
                borderSpacing: 0,
                backgroundColor: background,
                '& .MuiTableCell-root': {
                  fontFamily: 'Poppins, sans-serif',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>#</TableCell>
                  <TableCell sx={headerCellSx}>Amount</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx, index) => {
                  const isLast = index === transactions.length - 1;
                  return (
                    <TableRow key={tx.userTransactionId}>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          ...(isLast && { borderRadius: '0 0 0 4px' }),
                        }}
                      >
                        {tx.userTransactionId}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        ${tx.amount.toFixed(2)}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {tx.transferType || '-'}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {formattingDate(tx.createdAt)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          ...(isLast && { borderRadius: '0 0 4px 0' }),
                        }}
                      >
                        {tx.description || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Modal>
  );
};

export default OrderTransactionsModal;
